import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { postApi } from '@/lib/api';
import { CreatePostRequest, UpdatePostRequest, GeneratePostsRequest } from '@/types';
import { queryKeys, regeneratingAwareInterval } from './shared';

// Posts

export const usePosts = (
  workspaceId: string,
  filters?: { brandId?: string; platform?: string; status?: string },
) =>
  useQuery({
    queryKey: queryKeys.posts(workspaceId, filters as Record<string, string>),
    queryFn: () => postApi.list(workspaceId, filters),
    enabled: !!workspaceId,
    refetchInterval: regeneratingAwareInterval(false),
  });

/**
 * Paginated posts hook. Backed by the same controller as usePosts but
 * surfaces the X-Total-Count header so the caller can render a
 * pagination bar. Polish brief Step 1.
 */
export const usePostsPaged = (
  workspaceId: string,
  params: {
    brandId?: string;
    platform?: string;
    status?: string;
    /** V148 — media_format filter (FEED / REEL / CAROUSEL / STORY). */
    mediaFormat?: string;
    /** Feed sort by post date: 'oldest' = ascending; omit = newest-first. */
    sort?: string;
    page: number;
    size: number;
  },
) =>
  useQuery({
    queryKey: ['posts-paged', workspaceId, params],
    queryFn: () => postApi.listPaged(workspaceId, params),
    enabled: !!workspaceId,
    // Keep the previous page rendered while the next one loads so the grid
    // doesn't flash empty on page change. TanStack v5 renamed this to
    // `placeholderData: keepPreviousData` — equivalent behavior.
    placeholderData: (previous) => previous,
    refetchInterval: regeneratingAwareInterval(false),
  });

export const useCreatePost = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePostRequest) => postApi.create(workspaceId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posts', workspaceId] });
      qc.invalidateQueries({ queryKey: ['posts-paged', workspaceId] });
      qc.invalidateQueries({ queryKey: queryKeys.review(workspaceId) });
      qc.invalidateQueries({ queryKey: queryKeys.stats(workspaceId) });
    },
  });
};

export const useUpdatePost = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ postId, data }: { postId: string; data: UpdatePostRequest }) =>
      postApi.update(workspaceId, postId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posts', workspaceId] });
      qc.invalidateQueries({ queryKey: ['posts-paged', workspaceId] });
      qc.invalidateQueries({ queryKey: queryKeys.review(workspaceId) });
      qc.invalidateQueries({ queryKey: queryKeys.stats(workspaceId) });
    },
  });
};

export const useDeletePost = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    // Treat 404 as a soft-success: the row was already gone server-side
    // (e.g. another tab deleted it, or a reconciliation pass dropped a
    // DELETED_ON_PLATFORM post under a retention rule). Surfacing the
    // 404 just trains users that delete is broken — converging caches
    // is the desired end state, so we swallow + invalidate.
    mutationFn: async (postId: string) => {
      try {
        return await postApi.delete(workspaceId, postId);
      } catch (e: unknown) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404) return null;
        throw e;
      }
    },
    // PostsPage uses usePostsPaged (queryKey `['posts-paged', ...]`),
    // not usePosts (queryKey `['posts', ...]`). The bare `['posts',
    // workspaceId]` invalidate doesn't prefix-match the paged key, so
    // the deleted row stays visible until F5. Invalidate both
    // prefixes plus the review inbox (PostCard's delete can fire from
    // ReviewPage too).
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posts', workspaceId] });
      qc.invalidateQueries({ queryKey: ['posts-paged', workspaceId] });
      qc.invalidateQueries({ queryKey: queryKeys.review(workspaceId) });
      qc.invalidateQueries({ queryKey: queryKeys.stats(workspaceId) });
    },
  });
};

export const useBulkDeletePosts = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (postIds: string[]) => postApi.bulkDelete(workspaceId, postIds),
    // Same cache surfaces as useDeletePost — the bulk endpoint hits the
    // review inbox + both post lists + stats in one shot.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posts', workspaceId] });
      qc.invalidateQueries({ queryKey: ['posts-paged', workspaceId] });
      qc.invalidateQueries({ queryKey: queryKeys.review(workspaceId) });
      qc.invalidateQueries({ queryKey: queryKeys.stats(workspaceId) });
    },
  });
};

export const useGeneratePosts = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: GeneratePostsRequest) => postApi.generate(workspaceId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posts', workspaceId] });
      qc.invalidateQueries({ queryKey: queryKeys.stats(workspaceId) });
    },
  });
};
